import React, { useState, useMemo } from 'react';
import { Logo } from './icons/Logo';
import { BellIcon, SearchIcon, MenuIcon, XIcon, FireIcon, LogOutIcon, LayoutDashboardIcon, UserIcon } from './icons/InterfaceIcons';
import { calculateUserStats } from '../utils/userStats';
import { ThemeToggle } from './ThemeToggle';

export const TopNav = ({ 
  user, 
  isLeftRailOpen, 
  onMenuToggle, 
  showMenuButton = true, 
  onSignOut, 
  onNavigateToSignIn, 
  onNavigateToSignUp,
  onNavigateToHome,
  onNavigateToDashboard,
  onNavigateToProfile,
  theme,
  onThemeToggle
}) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  
  // A streak would typically come from the database, but we'll mock it for now.
  const weeklyStreak = 5;

  const handleLogoClick = (e) => {
    e.preventDefault();
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    if (newCount >= 7) {
      // Reset counter and navigate to admin
      setLogoClickCount(0);
      window.location.href = '/admin';
    } else {
      onNavigateToHome();
    }
    
    // Reset counter after 3 seconds of no clicks
    setTimeout(() => {
      setLogoClickCount(0);
    }, 3000);
  };

  return (
    <header className="sticky top-0 z-50 h-16 flex-shrink-0 bg-brand-surface/95 backdrop-blur-lg border-b border-brand-border">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Left Section */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {showMenuButton && (
              <button
                onClick={onMenuToggle}
                className="p-2 -ml-2 rounded-md text-brand-text-secondary hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-primary cursor-pointer"
                aria-expanded={isLeftRailOpen}
                aria-controls="left-rail"
                aria-label="Toggle filter menu"
              >
                <span className="sr-only">Toggle filter menu</span>
                {isLeftRailOpen ? (
                  <XIcon className="block h-6 w-6" />
                ) : (
                  <MenuIcon className="block h-6 w-6" />
                )}
              </button>
            )}
          
            <a href="#" onClick={handleLogoClick} className="flex items-center cursor-pointer" title="TEST SERIES">
              <Logo size="small" />
            </a>
            <div className="hidden md:flex items-center relative">
              <SearchIcon className="absolute left-3 h-5 w-5 text-brand-text-secondary" />
              <input
                type="text"
                placeholder="Search papers, chapters..."
                className="bg-brand-surface dark:bg-brand-bg border border-brand-border rounded-lg pl-10 pr-4 py-2 w-56 lg:w-96 text-sm text-brand-text-primary placeholder-brand-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-primary transition-all"
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {user ? (
              <>
                <div className="flex items-center space-x-1 text-brand-secondary font-semibold text-sm border border-brand-secondary/30 bg-brand-secondary/10 px-2.5 py-1 rounded-full">
                  <FireIcon className="h-4 w-4 text-brand-accent-amber"/>
                  <span>{weeklyStreak} day streak</span>
                </div>
                <button className="relative p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors cursor-pointer" aria-label="Notifications">
                  <BellIcon className="h-6 w-6 text-brand-text-secondary" />
                  <span className="absolute top-1 right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-accent-red opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-accent-red"></span>
                  </span>
                </button>
                <ThemeToggle theme={theme} onToggle={onThemeToggle} />
                <div className="relative">
                  <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="w-10 h-10 rounded-full cursor-pointer block">
                    <img src={user.photoURL || `https://i.pravatar.cc/100?u=${user.email}`} alt="User Avatar" className="rounded-full w-full h-full object-cover border-2 border-brand-primary cursor-pointer"/>
                  </button>
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-brand-surface rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-50">
                      <div className="px-4 py-3 border-b border-brand-border">
                        <p className="text-sm text-brand-text-secondary">Signed in as</p>
                        <p className="text-sm font-semibold text-brand-text-primary truncate">{user.name || user.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => { onNavigateToProfile(); setIsProfileMenuOpen(false); }}
                          className="w-full text-left flex items-center space-x-3 px-4 py-2 text-sm text-brand-text-secondary hover:bg-black/5 dark:hover:bg-white/10 hover:text-brand-text-primary cursor-pointer"
                        >
                          <UserIcon className="w-5 h-5"/>
                          <span>My Profile</span>
                        </button>
                        <button
                          onClick={() => { onNavigateToDashboard(); setIsProfileMenuOpen(false); }}
                          className="w-full text-left flex items-center space-x-3 px-4 py-2 text-sm text-brand-text-secondary hover:bg-black/5 dark:hover:bg-white/10 hover:text-brand-text-primary cursor-pointer"
                        >
                          <LayoutDashboardIcon className="w-5 h-5"/>
                          <span>Dashboard</span>
                        </button>
                         <button
                          onClick={() => { onSignOut(); setIsProfileMenuOpen(false); }}
                          className="w-full text-left flex items-center space-x-3 px-4 py-2 text-sm text-brand-text-secondary hover:bg-black/5 dark:hover:bg-white/10 hover:text-brand-text-primary cursor-pointer"
                        >
                          <LogOutIcon className="w-5 h-5"/>
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <ThemeToggle theme={theme} onToggle={onThemeToggle} />
                <button onClick={onNavigateToSignIn} className="text-sm font-semibold text-brand-primary hover:text-brand-primary-hover transition-colors px-4 py-2 rounded-lg cursor-pointer">
                  Sign In
                </button>
                <button onClick={onNavigateToSignUp} className="bg-brand-primary hover:bg-brand-primary-hover text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors cursor-pointer">
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};